/**
 * Pure game-state helpers for a Sukoji board.
 *
 * Preferred puzzle shape:
 * {
 *   id: "2026-08-11",
 *   geometry: {
 *     width: 5,
 *     height: 5,
 *     blocks: boolean[][],
 *     // Optional. When omitted, maximal horizontal/vertical segments are
 *     // derived from `blocks`.
 *     segments: [{ id, direction, cells: [[row, column], ...] }]
 *   },
 *   givens: number[][],
 *   solution: number[][] // optional
 * }
 *
 * A compact signed-grid representation is also accepted:
 * 0 is a block, a positive number is a given and a negative number is an
 * editable cell whose absolute value is the solution.
 */

export const HISTORY_LIMIT = 500;
export const GAME_STATE_SCHEMA_VERSION = 1;

const EMPTY = 0;

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function positiveInteger(value) {
  return Number.isInteger(value) && value > 0 ? value : 0;
}

function matrixDimensions(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return null;
  const width = Array.isArray(matrix[0]) ? matrix[0].length : 0;
  if (width === 0 || matrix.some((row) => !Array.isArray(row) || row.length !== width)) {
    return null;
  }
  return { width, height: matrix.length };
}

function firstMatrix(...candidates) {
  return candidates.find((candidate) => matrixDimensions(candidate)) ?? null;
}

function dimensionsOf(rawPuzzle) {
  const geometry = isObject(rawPuzzle.geometry) ? rawPuzzle.geometry : {};
  const mask = rawPuzzle.mask ?? geometry.mask;
  const maskDimensions = Array.isArray(mask) && mask.length > 0 && mask.every((row) => typeof row === 'string')
    ? {
        width: mask[0].length,
        height: mask.length,
        valid: mask[0].length > 0 && mask.every((row) => row.length === mask[0].length),
      }
    : null;
  if (maskDimensions && !maskDimensions.valid) {
    throw new TypeError('Puzzle mask rows must all have the same non-zero width.');
  }
  const matrix = firstMatrix(
    rawPuzzle.grid,
    rawPuzzle.givens,
    rawPuzzle.solution,
    rawPuzzle.cells,
    geometry.blocks,
    geometry.blocked,
    rawPuzzle.blocks,
    rawPuzzle.blocked,
  );
  const inferred = matrixDimensions(matrix);
  const width = Number(
    geometry.width ?? rawPuzzle.width ?? rawPuzzle.size_w ?? maskDimensions?.width ?? inferred?.width,
  );
  const height = Number(
    geometry.height ?? rawPuzzle.height ?? rawPuzzle.size_h ?? maskDimensions?.height ?? inferred?.height,
  );

  if (!Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0) {
    throw new TypeError('Puzzle geometry must define a positive width and height.');
  }
  return { width, height };
}

function emptyMatrix(height, width, factory) {
  return Array.from({ length: height }, (_, row) =>
    Array.from({ length: width }, (_, column) => factory(row, column)),
  );
}

function isBlockMarker(value) {
  if (value === true || value === '#') return true;
  if (typeof value === 'string') return value.toLowerCase() === 'block';
  return isObject(value) && (value.block === true || value.type === 'block');
}

function applyBlockSource(blocked, source, width, height) {
  if (!source) return false;
  let applied = false;
  const dimensions = matrixDimensions(source);

  if (dimensions?.width === width && dimensions.height === height) {
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        if (isBlockMarker(source[row][column])) {
          blocked[row][column] = true;
          applied = true;
        }
      }
    }
    return applied;
  }

  if (Array.isArray(source)) {
    for (const coordinate of source) {
      const cell = normalizeCoordinate(coordinate, width, height);
      if (cell) {
        blocked[cell.row][cell.column] = true;
        applied = true;
      }
    }
    return applied;
  }

  if (isObject(source)) {
    for (const [key, value] of Object.entries(source)) {
      if (!value) continue;
      const cell = normalizeCoordinate(key, width, height);
      if (cell) {
        blocked[cell.row][cell.column] = true;
        applied = true;
      }
    }
  }
  return applied;
}

function normalizeCoordinate(value, width, height) {
  let row;
  let column;

  if (Array.isArray(value) && value.length >= 2) {
    [row, column] = value;
  } else if (typeof value === 'string') {
    const match = value.match(/^\s*(\d+)\s*[:,]\s*(\d+)\s*$/);
    if (!match) return null;
    row = Number(match[1]);
    column = Number(match[2]);
  } else if (Number.isInteger(value)) {
    row = Math.floor(value / width);
    column = value % width;
  } else if (isObject(value)) {
    row = value.row ?? value.r ?? value.y;
    column = value.column ?? value.col ?? value.c ?? value.x;
  }

  row = Number(row);
  column = Number(column);
  if (
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    row < 0 ||
    row >= height ||
    column < 0 ||
    column >= width
  ) {
    return null;
  }
  return { row, column };
}

function signedGrid(rawPuzzle, width, height) {
  const dimensions = matrixDimensions(rawPuzzle.grid);
  if (dimensions?.width !== width || dimensions.height !== height) return null;
  const values = rawPuzzle.grid.flat();
  return values.some((value) => typeof value === 'number' && value < 0)
    ? rawPuzzle.grid
    : null;
}

function buildBlocked(rawPuzzle, width, height, encodedGrid) {
  const geometry = isObject(rawPuzzle.geometry) ? rawPuzzle.geometry : {};
  const blocked = emptyMatrix(height, width, () => false);
  let explicitBlocks = false;

  const mask = rawPuzzle.mask ?? geometry.mask;
  if (Array.isArray(mask) && mask.length === height && mask.every((row) => typeof row === 'string' && row.length === width)) {
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        blocked[row][column] = mask[row][column] === '#';
      }
    }
    explicitBlocks = true;
  }

  for (const source of [
    geometry.blocks,
    geometry.blocked,
    rawPuzzle.blocks,
    rawPuzzle.blocked,
  ]) {
    explicitBlocks = applyBlockSource(blocked, source, width, height) || explicitBlocks;
  }

  const cells = rawPuzzle.cells ?? geometry.cells;
  const cellDimensions = matrixDimensions(cells);
  if (cellDimensions?.width === width && cellDimensions.height === height) {
    explicitBlocks = applyBlockSource(blocked, cells, width, height) || explicitBlocks;
  }

  if (encodedGrid && !explicitBlocks) {
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        blocked[row][column] = encodedGrid[row][column] === 0;
      }
    }
  }
  return blocked;
}

function readValueSource(source, row, column) {
  const dimensions = matrixDimensions(source);
  if (dimensions) return source[row]?.[column];
  if (isObject(source)) {
    return source[`${row}:${column}`] ?? source[`${row},${column}`];
  }
  return undefined;
}

function valueFromCell(cell, kind) {
  if (!isObject(cell)) return undefined;
  if (kind === 'given') {
    if (cell.fixed === true) return cell.value;
    return cell.given ?? cell.clue;
  }
  return cell.solution ?? cell.answer;
}

function buildNumberMatrix(rawPuzzle, blocked, width, height, encodedGrid, kind) {
  const source = kind === 'given'
    ? rawPuzzle.givens ?? rawPuzzle.clues
    : rawPuzzle.solution ?? rawPuzzle.answer;
  const cells = rawPuzzle.cells;

  return emptyMatrix(height, width, (row, column) => {
    if (blocked[row][column]) return EMPTY;
    let value = readValueSource(source, row, column);
    if (value === undefined && matrixDimensions(cells)) {
      value = valueFromCell(cells[row][column], kind);
    }
    if (value === undefined && encodedGrid) {
      const encoded = encodedGrid[row][column];
      value = kind === 'given' ? (encoded > 0 ? encoded : 0) : Math.abs(encoded);
    }
    return positiveInteger(Number(value));
  });
}

function deriveSegments(blocked, width, height) {
  const segments = [];
  let nextId = 0;

  for (let row = 0; row < height; row += 1) {
    let cells = [];
    for (let column = 0; column <= width; column += 1) {
      if (column < width && !blocked[row][column]) {
        cells.push({ row, column });
      } else {
        if (cells.length >= 2) {
          segments.push({ id: `h-${nextId++}`, direction: 'horizontal', cells });
        }
        cells = [];
      }
    }
  }

  for (let column = 0; column < width; column += 1) {
    let cells = [];
    for (let row = 0; row <= height; row += 1) {
      if (row < height && !blocked[row][column]) {
        cells.push({ row, column });
      } else {
        if (cells.length >= 2) {
          segments.push({ id: `v-${nextId++}`, direction: 'vertical', cells });
        }
        cells = [];
      }
    }
  }
  return segments;
}

function normalizeExplicitSegments(source, blocked, width, height) {
  if (!Array.isArray(source)) return null;
  const normalized = [];
  const signatures = new Set();

  for (let index = 0; index < source.length; index += 1) {
    const raw = source[index];
    const rawCells = Array.isArray(raw) ? raw : raw?.cells;
    if (!Array.isArray(rawCells)) continue;
    const seen = new Set();
    const cells = [];
    for (const rawCell of rawCells) {
      const cell = normalizeCoordinate(rawCell, width, height);
      if (!cell || blocked[cell.row][cell.column]) continue;
      const key = `${cell.row}:${cell.column}`;
      if (!seen.has(key)) {
        seen.add(key);
        cells.push(cell);
      }
    }
    if (cells.length < 2) continue;

    const sameRow = cells.every((cell) => cell.row === cells[0].row);
    const sameColumn = cells.every((cell) => cell.column === cells[0].column);
    if (!sameRow && !sameColumn) {
      throw new TypeError(`Segment ${index} is neither horizontal nor vertical.`);
    }
    cells.sort((left, right) =>
      sameRow ? left.column - right.column : left.row - right.row,
    );
    for (let position = 1; position < cells.length; position += 1) {
      const previous = cells[position - 1];
      const current = cells[position];
      const consecutive = sameRow
        ? current.column === previous.column + 1
        : current.row === previous.row + 1;
      if (!consecutive) throw new TypeError(`Segment ${index} is not contiguous.`);
    }

    const signature = cells.map((cell) => `${cell.row}:${cell.column}`).join('|');
    if (signatures.has(signature)) continue;
    signatures.add(signature);
    normalized.push({
      id: String(raw?.id ?? `${sameRow ? 'h' : 'v'}-${index}`),
      direction: raw?.direction ?? (sameRow ? 'horizontal' : 'vertical'),
      cells,
    });
  }
  return normalized;
}

function fnv1a(value) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function makeFingerprint(puzzle) {
  const mask = puzzle.blocked.flat().map((value) => (value ? '1' : '0')).join('');
  const givens = puzzle.givens.flat().join(',');
  const segments = puzzle.segments
    .map((segment) => segment.cells.map((cell) => `${cell.row}.${cell.column}`).join('-'))
    .sort()
    .join('/');
  return `${puzzle.id}:${fnv1a(`${puzzle.width}x${puzzle.height}|${mask}|${givens}|${segments}`)}`;
}

/** Normalize the accepted puzzle representations into one stable geometry. */
export function normalizePuzzle(rawPuzzle) {
  if (!isObject(rawPuzzle)) throw new TypeError('A puzzle object is required.');
  const { width, height } = dimensionsOf(rawPuzzle);
  const encodedGrid = signedGrid(rawPuzzle, width, height);
  const blocked = buildBlocked(rawPuzzle, width, height, encodedGrid);
  const givens = buildNumberMatrix(rawPuzzle, blocked, width, height, encodedGrid, 'given');
  const solution = buildNumberMatrix(rawPuzzle, blocked, width, height, encodedGrid, 'solution');
  const explicitSegments = rawPuzzle.geometry?.segments ?? rawPuzzle.segments;
  const segments = normalizeExplicitSegments(explicitSegments, blocked, width, height)
    ?? deriveSegments(blocked, width, height);

  const cellSegments = emptyMatrix(height, width, () => []);
  segments.forEach((segment, segmentIndex) => {
    segment.length = segment.cells.length;
    for (const cell of segment.cells) {
      cellSegments[cell.row][cell.column].push(segmentIndex);
    }
  });

  for (let row = 0; row < height; row += 1) {
    for (let column = 0; column < width; column += 1) {
      if (!blocked[row][column] && cellSegments[row][column].length === 0) {
        throw new TypeError(`Playable cell ${row}:${column} belongs to no segment of length >= 2.`);
      }
    }
  }

  // The input range follows the board dimensions, not the local segment
  // lengths. Segment-specific limits are enforced separately as conflicts.
  const maxDigit = Math.max(width, height);
  const normalized = {
    id: String(rawPuzzle.id ?? rawPuzzle.date ?? rawPuzzle.name ?? 'puzzle'),
    width,
    height,
    maxDigit,
    blocked,
    givens,
    solution: solution.flat().some(Boolean) ? solution : null,
    segments,
    cellSegments,
  };
  normalized.fingerprint = makeFingerprint(normalized);
  return normalized;
}

function initialSnapshot(puzzle) {
  return {
    values: emptyMatrix(puzzle.height, puzzle.width, (row, column) =>
      puzzle.blocked[row][column] ? EMPTY : puzzle.givens[row][column],
    ),
    notes: emptyMatrix(puzzle.height, puzzle.width, () => []),
  };
}

function cloneSnapshot(snapshot) {
  return {
    values: snapshot.values.map((row) => [...row]),
    notes: snapshot.notes.map((row) => row.map((notes) => [...notes])),
  };
}

/** Create a fresh state. Past and future intentionally start empty. */
export function createGameState(rawPuzzle) {
  const puzzle = normalizePuzzle(rawPuzzle);
  return {
    schemaVersion: GAME_STATE_SCHEMA_VERSION,
    puzzle,
    past: [],
    present: initialSnapshot(puzzle),
    future: [],
  };
}

function assertState(state) {
  if (!isObject(state) || !state.puzzle || !state.present) {
    throw new TypeError('A valid game state is required.');
  }
}

function actionCoordinates(rowOrCell, columnOrValue, maybeValue) {
  if (isObject(rowOrCell) || Array.isArray(rowOrCell)) {
    const row = Array.isArray(rowOrCell) ? rowOrCell[0] : rowOrCell.row ?? rowOrCell.y;
    const column = Array.isArray(rowOrCell)
      ? rowOrCell[1]
      : rowOrCell.column ?? rowOrCell.col ?? rowOrCell.x;
    return { row: Number(row), column: Number(column), value: columnOrValue };
  }
  return { row: Number(rowOrCell), column: Number(columnOrValue), value: maybeValue };
}

function editableCell(state, row, column) {
  const { puzzle } = state;
  return (
    Number.isInteger(row) &&
    Number.isInteger(column) &&
    row >= 0 &&
    row < puzzle.height &&
    column >= 0 &&
    column < puzzle.width &&
    !puzzle.blocked[row][column] &&
    puzzle.givens[row][column] === EMPTY
  );
}

function commit(state, nextPresent) {
  return {
    ...state,
    past: [...state.past, cloneSnapshot(state.present)].slice(-HISTORY_LIMIT),
    present: nextPresent,
    future: [],
  };
}

/** Return the legal candidate values imposed by the cell's incident segments. */
export function getCellDomain(stateOrPuzzle, rowOrCell, maybeColumn) {
  const puzzle = stateOrPuzzle?.puzzle ?? stateOrPuzzle;
  const position = actionCoordinates(rowOrCell, maybeColumn, undefined);
  const { row, column } = position;
  if (
    !puzzle ||
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    puzzle.blocked?.[row]?.[column] !== false
  ) {
    return [];
  }
  const incident = puzzle.cellSegments[row][column];
  const maximum = Math.min(...incident.map((index) => puzzle.segments[index].length));
  return Array.from({ length: maximum }, (_, index) => index + 1);
}

/** Enter a digit. Conflicting but globally valid digits are retained and flagged separately. */
export function inputDigit(state, rowOrCell, columnOrDigit, maybeDigit) {
  assertState(state);
  const { row, column, value } = actionCoordinates(rowOrCell, columnOrDigit, maybeDigit);
  const digit = Number(value);
  if (digit === EMPTY) return eraseCell(state, row, column);
  if (!editableCell(state, row, column)) return state;
  if (!Number.isInteger(digit) || digit < 1 || digit > state.puzzle.maxDigit) return state;
  if (state.present.values[row][column] === digit && state.present.notes[row][column].length === 0) {
    return state;
  }

  const next = cloneSnapshot(state.present);
  next.values[row][column] = digit;
  next.notes[row][column] = [];
  return commit(state, next);
}

export const setDigit = inputDigit;

/** Toggle one pencil mark. Notes are kept sorted and unique. */
export function toggleNote(state, rowOrCell, columnOrNote, maybeNote) {
  assertState(state);
  const { row, column, value } = actionCoordinates(rowOrCell, columnOrNote, maybeNote);
  const note = Number(value);
  if (!editableCell(state, row, column) || state.present.values[row][column] !== EMPTY) {
    return state;
  }
  if (!Number.isInteger(note) || note < 1 || note > state.puzzle.maxDigit) return state;

  const current = state.present.notes[row][column];
  const nextNotes = current.includes(note)
    ? current.filter((candidate) => candidate !== note)
    : [...current, note].sort((left, right) => left - right);
  const next = cloneSnapshot(state.present);
  next.notes[row][column] = nextNotes;
  return commit(state, next);
}

/** Clear both the digit and pencil marks in an editable cell. */
export function eraseCell(state, rowOrCell, maybeColumn) {
  assertState(state);
  const { row, column } = actionCoordinates(rowOrCell, maybeColumn, undefined);
  if (!editableCell(state, row, column)) return state;
  if (
    state.present.values[row][column] === EMPTY &&
    state.present.notes[row][column].length === 0
  ) {
    return state;
  }

  const next = cloneSnapshot(state.present);
  next.values[row][column] = EMPTY;
  next.notes[row][column] = [];
  return commit(state, next);
}

/** Clear every player-entered digit and note as one undoable action. */
export function resetPlayerEntries(state) {
  assertState(state);
  let hasPlayerEntries = false;

  for (let row = 0; row < state.puzzle.height && !hasPlayerEntries; row += 1) {
    for (let column = 0; column < state.puzzle.width; column += 1) {
      if (
        editableCell(state, row, column)
        && (
          state.present.values[row][column] !== EMPTY
          || state.present.notes[row][column].length > 0
        )
      ) {
        hasPlayerEntries = true;
        break;
      }
    }
  }

  return hasPlayerEntries ? commit(state, initialSnapshot(state.puzzle)) : state;
}

export function undo(state) {
  assertState(state);
  if (state.past.length === 0) return state;
  const previous = state.past[state.past.length - 1];
  return {
    ...state,
    past: state.past.slice(0, -1),
    present: cloneSnapshot(previous),
    future: [cloneSnapshot(state.present), ...state.future].slice(0, HISTORY_LIMIT),
  };
}

export function redo(state) {
  assertState(state);
  if (state.future.length === 0) return state;
  const [next, ...remaining] = state.future;
  return {
    ...state,
    past: [...state.past, cloneSnapshot(state.present)].slice(-HISTORY_LIMIT),
    present: cloneSnapshot(next),
    future: remaining,
  };
}

function addConflict(conflicts, cell, reason, detail) {
  const key = `${cell.row}:${cell.column}`;
  let conflict = conflicts.get(key);
  if (!conflict) {
    conflict = { row: cell.row, column: cell.column, key, reasons: [], details: [] };
    conflicts.set(key, conflict);
  }
  if (!conflict.reasons.includes(reason)) conflict.reasons.push(reason);
  conflict.details.push(detail);
}

/**
 * Detect local rule violations. Incomplete cells are not conflicts.
 * Returned reasons are `out-of-range` and/or `duplicate`.
 */
export function detectConflicts(state) {
  assertState(state);
  const conflicts = new Map();

  for (const segment of state.puzzle.segments) {
    const occurrences = new Map();
    for (const cell of segment.cells) {
      const value = state.present.values[cell.row][cell.column];
      if (!Number.isInteger(value) || value <= EMPTY) continue;
      if (value > segment.length) {
        addConflict(conflicts, cell, 'out-of-range', {
          type: 'out-of-range',
          segmentId: segment.id,
          value,
          maximum: segment.length,
        });
      }
      const cells = occurrences.get(value) ?? [];
      cells.push(cell);
      occurrences.set(value, cells);
    }

    for (const [value, cells] of occurrences) {
      if (cells.length < 2) continue;
      for (const cell of cells) {
        addConflict(conflicts, cell, 'duplicate', {
          type: 'duplicate',
          segmentId: segment.id,
          value,
        });
      }
    }
  }
  return [...conflicts.values()].sort((left, right) =>
    left.row - right.row || left.column - right.column,
  );
}

export const getConflicts = detectConflicts;

export function getConflictKeys(state) {
  return new Set(detectConflicts(state).map((conflict) => conflict.key));
}

export function hasConflictAt(state, rowOrCell, maybeColumn) {
  const { row, column } = actionCoordinates(rowOrCell, maybeColumn, undefined);
  return getConflictKeys(state).has(`${row}:${column}`);
}

/** A grid is complete when every playable cell is filled and every segment is valid. */
export function isComplete(state) {
  assertState(state);
  for (let row = 0; row < state.puzzle.height; row += 1) {
    for (let column = 0; column < state.puzzle.width; column += 1) {
      if (
        !state.puzzle.blocked[row][column] &&
        !positiveInteger(state.present.values[row][column])
      ) {
        return false;
      }
    }
  }
  return detectConflicts(state).length === 0;
}

function snapshotHasDimensions(snapshot, puzzle) {
  const valueDimensions = matrixDimensions(snapshot?.values);
  const noteDimensions = matrixDimensions(snapshot?.notes);
  return (
    valueDimensions?.width === puzzle.width &&
    valueDimensions.height === puzzle.height &&
    noteDimensions?.width === puzzle.width &&
    noteDimensions.height === puzzle.height
  );
}

function sanitizeSnapshot(raw, puzzle, strict) {
  if (!isObject(raw)) return null;
  if (strict && !snapshotHasDimensions(raw, puzzle)) return null;

  const values = emptyMatrix(puzzle.height, puzzle.width, (row, column) => {
    if (puzzle.blocked[row][column]) return EMPTY;
    if (puzzle.givens[row][column]) return puzzle.givens[row][column];
    const value = Number(raw.values?.[row]?.[column]);
    return Number.isInteger(value) && value > EMPTY && value <= puzzle.maxDigit
      ? value
      : EMPTY;
  });

  const notes = emptyMatrix(puzzle.height, puzzle.width, (row, column) => {
    if (
      puzzle.blocked[row][column] ||
      puzzle.givens[row][column] ||
      values[row][column] !== EMPTY
    ) {
      return [];
    }
    const rawNotes = raw.notes?.[row]?.[column];
    if (!Array.isArray(rawNotes)) return [];
    return [...new Set(rawNotes.map(Number).filter(
      (note) => Number.isInteger(note) && note >= 1 && note <= puzzle.maxDigit,
    ))]
      .sort((left, right) => left - right);
  });
  return { values, notes };
}

/** Serialize only mutable state; puzzle geometry remains the source of truth. */
export function serializeGameState(state) {
  assertState(state);
  return JSON.stringify({
    schemaVersion: GAME_STATE_SCHEMA_VERSION,
    puzzleId: state.puzzle.id,
    puzzleFingerprint: state.puzzle.fingerprint,
    past: state.past.slice(-HISTORY_LIMIT).map(cloneSnapshot),
    present: cloneSnapshot(state.present),
    future: state.future.slice(0, HISTORY_LIMIT).map(cloneSnapshot),
  });
}

/**
 * Restore a saved state against a trusted puzzle. Malformed data falls back to
 * a fresh state; malformed history entries are discarded independently.
 */
export function restoreGameState(rawPuzzle, serialized) {
  const fresh = createGameState(rawPuzzle);
  let payload = serialized;

  try {
    if (typeof serialized === 'string') {
      if (serialized.length > 5_000_000) return fresh;
      payload = JSON.parse(serialized);
    }
  } catch {
    return fresh;
  }
  if (!isObject(payload)) return fresh;
  if (
    payload.puzzleId !== undefined &&
    String(payload.puzzleId) !== fresh.puzzle.id
  ) {
    return fresh;
  }
  if (
    payload.puzzleFingerprint !== undefined &&
    payload.puzzleFingerprint !== fresh.puzzle.fingerprint
  ) {
    return fresh;
  }

  const present = sanitizeSnapshot(payload.present, fresh.puzzle, false);
  if (!present) return fresh;
  const past = Array.isArray(payload.past)
    ? payload.past
      .slice(-HISTORY_LIMIT)
      .map((snapshot) => sanitizeSnapshot(snapshot, fresh.puzzle, true))
      .filter(Boolean)
    : [];
  const future = Array.isArray(payload.future)
    ? payload.future
      .slice(0, HISTORY_LIMIT)
      .map((snapshot) => sanitizeSnapshot(snapshot, fresh.puzzle, true))
      .filter(Boolean)
    : [];

  return { ...fresh, past, present, future };
}

/** Alternate argument order convenient for JSON parsers and storage adapters. */
export function deserializeGameState(serialized, rawPuzzle) {
  return restoreGameState(rawPuzzle, serialized);
}
